import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, DollarSign, Wallet, Calendar, LogOut, Plus, ChevronLeft, ChevronRight,
  Tags, RefreshCw, Package, Loader2, Edit, Trash2, Check, X, Layers, MoreHorizontal, Clock, AlertTriangle, Settings,
  SlidersHorizontal, ArrowUpDown, ArrowUp, ArrowDown, Eye, EyeOff, Target, Bot, Search, ClipboardList
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";
import { format, parseISO, isAfter, isBefore, isToday } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

// Month keys for translation
const MONTH_KEYS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];


// Helper to format dates in UTC to avoid timezone conversion issues
const formatDateUTC = (dateString, formatStr = "MMM d, yyyy") => {
  if (!dateString) return "-";
  try {
    return formatInTimeZone(parseISO(dateString), "UTC", formatStr);
  } catch {
    return format(parseISO(dateString), formatStr);
  }
};

export default function UserDashboard() {
  const { user, logout, updateUser } = useAuth();
  const { brandName } = useBrand();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [overview, setOverview] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [packages, setPackages] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [userSettings, setUserSettings] = useState(null);
  const [goalsSummary, setGoalsSummary] = useState(null);
  const [disabledIndividualPages, setDisabledIndividualPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    date: true,
    name: true,
    category: true,
    type: true,
    original: true,
    converted: true,
    balance: true,
    status: true,
    recurring: true,
    nextDue: true,
    description: true,
    actions: true
  });
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  
  // Row selection state
  const [selectedRows, setSelectedRows] = useState(new Set());

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [overviewRes, categoriesRes, subgroupsRes, packagesRes, currenciesRes, settingsRes, goalsSummaryRes, disabledPagesRes] = await Promise.all([
        api.get(`/overview/month?year=${selectedYear}&month=${selectedMonth}`),
        api.get("/categories"),
        api.get("/subgroups"),
        api.get("/packages"),
        api.get("/currencies"),
        api.get("/settings"),
        api.get("/goals/summary"),
        api.get("/individual-pages/disabled").catch(() => ({ data: { disabled_pages: [] } }))
      ]);
      setOverview(overviewRes.data);
      setCategories(categoriesRes.data);
      setSubgroups(subgroupsRes.data);
      setPackages(packagesRes.data);
      setCurrencies(currenciesRes.data);
      setUserSettings(settingsRes.data);
      setGoalsSummary(goalsSummaryRes.data);
      setDisabledIndividualPages(disabledPagesRes?.data?.disabled_pages || []);
    } catch (error) {
      toast.error(t("failed_to_load", "errors"));
    } finally {
      setIsLoading(false);
    }
  }, [selectedYear, selectedMonth, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    // Show package selection if user has no package
    if (user && !user.package_id) {
      setShowPackageDialog(true);
    }
  }, [user]);

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({ ...prev, [columnKey]: !prev[columnKey] }));
  };

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sort icon for column header
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  // Column definitions for the filter menu - using translation keys
  const columnDefs = [
    { key: 'date', labelKey: 'date' },
    { key: 'name', labelKey: 'name' },
    { key: 'category', labelKey: 'category' },
    { key: 'type', labelKey: 'type' },
    { key: 'original', labelKey: 'original' },
    { key: 'converted', labelKey: 'converted', requiresPreferredCurrency: true },
    { key: 'balance', labelKey: 'balance' },
    { key: 'status', labelKey: 'status' },
    { key: 'recurring', labelKey: 'recurring' },
    { key: 'nextDue', labelKey: 'next_due' },
    { key: 'description', labelKey: 'description' },
    { key: 'actions', labelKey: 'actions' }
  ];

  const handleMonthChange = (direction) => {
    setSelectedRows(new Set()); // Clear selection on month change
    if (direction === "prev") {
      if (selectedMonth === 1) {
        setSelectedMonth(12);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 12) {
        setSelectedMonth(1);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  // Toggle single row selection
  const toggleRowSelection = (txId) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(txId)) {
        newSet.delete(txId);
      } else {
        newSet.add(txId);
      }
      return newSet;
    });
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedRows(new Set());
  };

  const handleSelectPackage = async (packageId) => {
    try {
      const response = await api.post("/packages/select", { package_id: packageId });
      updateUser(response.data);
      setShowPackageDialog(false);
      toast.success(t("package_selected", "dashboard"));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("failed_to_save", "errors"));
    }
  };

  const handleTogglePaid = async (transactionId) => {
    try {
      await api.patch(`/transactions/${transactionId}/toggle-paid`);
      toast.success(t("payment_updated", "dashboard"));
      fetchData();
    } catch (error) {
      toast.error(t("failed_to_save", "errors"));
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success(t("logged_out", "dashboard"));
  };

  const usagePercentage = user?.monthly_limit && user.monthly_limit > 0 ? Math.min((user.monthly_usage / user.monthly_limit) * 100, 100) : 0;
  const isUnlimited = user?.monthly_limit === 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-[1600px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="size-8 rounded-lg bg-[hsl(var(--ff-primary))] flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <span className="font-semibold text-lg">{brandName}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-secondary rounded-lg">
              <Button variant="ghost" size="icon" onClick={() => handleMonthChange("prev")} data-testid="month-prev-button">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium px-2" data-testid="month-picker-button">
                {t(MONTH_KEYS[selectedMonth - 1], "dashboard")} {selectedYear}
              </span>
              <Button variant="ghost" size="icon" onClick={() => handleMonthChange("next")} data-testid="month-next-button">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Button onClick={() => setShowTransactionDialog(true)} data-testid="add-transaction-button">
              <Plus className="h-4 w-4 mr-2" /> {t("add_transaction", "dashboard")}
            </Button>
            <Button variant="outline" onClick={() => navigate("/goals")} data-testid="goals-button">
              <Target className="h-4 w-4 mr-2" /> {t("goals", "goals")}
            </Button>
            <Button variant="outline" onClick={() => navigate("/ai-chat")} data-testid="ai-chat-button" className="bg-primary/5 border-primary/20 hover:bg-primary/10">
              <Bot className="h-4 w-4 mr-2 text-primary" /> AI Assistant
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate("/settings")} data-testid="settings-button">
              <Settings className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-6 py-6 space-y-6">
        {/* Welcome & Package Info */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">{t("welcome_back", "auth")}</h1>
            <p className="text-muted-foreground">{user?.email}</p>
          </div>
          {user?.package_name && (
            <Card className="w-full md:w-auto" data-testid="package-limit-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-6">
                  <div>
                    <p className="text-sm font-medium">{user.package_name} {t("plan", "common")}</p>
                    <p className="text-xs text-muted-foreground">
                      {isUnlimited ? `${user.monthly_usage} transactions (Unlimited)` : `${user.monthly_usage} / ${user.monthly_limit} transactions`}
                    </p>
                  </div>
                  {!isUnlimited && (
                    <div className="w-24">
                      <Progress value={usagePercentage} className="h-2" data-testid="package-limit-progress" />
                    </div>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setShowPackageDialog(true)}>
                    <Package className="h-4 w-4 mr-1" /> {t("change", "common")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* KPI Cards */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="kpi-cards-loading">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-4 w-20 mb-2" />
                  <Skeleton className="h-8 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="kpi-cards">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t("total_income", "dashboard")}</p>
                  <div className="size-8 rounded-full income-bg flex items-center justify-center">
                    <TrendingUp className="h-4 w-4 income-text" />
                  </div>
                </div>
                <p className="text-2xl font-bold font-numeric mt-2 income-text" data-testid="total-income-value">
                  {(() => {
                    const symbol = userSettings?.preferred_currency
                      ? (currencies.find(c => c.code === userSettings.preferred_currency)?.symbol || '$')
                      : (currencies.find(c => c.code === userSettings?.default_currency)?.symbol || '$');
                    return `${symbol}${overview?.total_income?.toFixed(2) || "0.00"}`;
                  })()}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t("total_expenses", "dashboard")}</p>
                  <div className="size-8 rounded-full expense-bg flex items-center justify-center">
                    <TrendingDown className="h-4 w-4 expense-text" />
                  </div>
                </div>
                <p className="text-2xl font-bold font-numeric mt-2 expense-text" data-testid="total-expenses-value">
                  {(() => {
                    const symbol = userSettings?.preferred_currency
                      ? (currencies.find(c => c.code === userSettings.preferred_currency)?.symbol || '$')
                      : (currencies.find(c => c.code === userSettings?.default_currency)?.symbol || '$');
                    return `${symbol}${overview?.total_expenses?.toFixed(2) || "0.00"}`;
                  })()}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t("balance", "dashboard")}</p>
                  <div className="size-8 rounded-full bg-[hsl(var(--ff-primary)/.1)] flex items-center justify-center">
                    <Wallet className="h-4 w-4 text-[hsl(var(--ff-primary))]" />
                  </div>
                </div>
                <p className={`text-2xl font-bold font-numeric mt-2 ${overview?.balance >= 0 ? 'income-text' : 'expense-text'}`} data-testid="monthly-balance-value">
                  {(() => {
                    const symbol = userSettings?.preferred_currency
                      ? (currencies.find(c => c.code === userSettings.preferred_currency)?.symbol || '$')
                      : (currencies.find(c => c.code === userSettings?.default_currency)?.symbol || '$');
                    return `${symbol}${overview?.balance?.toFixed(2) || "0.00"}`;
                  })()}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{t("payment_status", "dashboard")}</p>
                  <div className="size-8 rounded-full bg-[hsl(var(--ff-info)/.1)] flex items-center justify-center">
                    <DollarSign className="h-4 w-4 text-[hsl(var(--ff-info))]" />
                  </div>
                </div>
                <div className="flex gap-3 mt-2">
                  <Badge variant="outline" className="status-paid" data-testid="paid-count-badge">
                    {overview?.paid_count || 0} {t("paid", "dashboard")}
                  </Badge>
                  <Badge variant="outline" className="status-pending" data-testid="unpaid-count-badge">
                    {overview?.unpaid_count || 0} {t("pending", "dashboard")}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Goals Summary Card */}
        {!isLoading && goalsSummary && goalsSummary.total_goals > 0 && (
          <Card 
            className="border-l-4 border-l-[hsl(var(--ff-primary))] cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate("/goals")}
            data-testid="goals-summary-card"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="size-10 rounded-full bg-[hsl(var(--ff-primary)/.1)] flex items-center justify-center">
                    <Target className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{t("financial_goals", "goals")}</h3>
                    <p className="text-sm text-muted-foreground">
                      {goalsSummary.active_goals} active • {goalsSummary.overall_progress?.toFixed(0)}% overall progress
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">{t("saved", "goals")}</p>
                    <p className="font-bold font-numeric income-text">
                      {(() => {
                        const symbol = currencies.find(c => c.code === goalsSummary.currency)?.symbol || '$';
                        return `${symbol}${goalsSummary.total_saved?.toLocaleString()}`;
                      })()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">{t("target", "goals")}</p>
                    <p className="font-bold font-numeric">
                      {(() => {
                        const symbol = currencies.find(c => c.code === goalsSummary.currency)?.symbol || '$';
                        return `${symbol}${goalsSummary.total_target?.toLocaleString()}`;
                      })()}
                    </p>
                  </div>
                  <Progress value={goalsSummary.overall_progress || 0} className="w-24 h-2" />
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Product Governance Matrix Card */}
        {!isLoading && !disabledIndividualPages.includes('product-governance') && (
          <Card 
            className="border-l-4 border-l-slate-500 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate("/product-governance")}
            data-testid="product-governance-card"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="size-10 rounded-full bg-slate-100 dark:bg-slate-900/30 flex items-center justify-center">
                    <ClipboardList className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{t("title", "product_governance") || "Product Governance Matrix"}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t("description", "product_governance") || "Organize, prioritize, and track product requirements, improvements, and issues."}
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions Row */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowCategoryDialog(true)} data-testid="manage-categories-button">
            <Tags className="h-4 w-4 mr-2" /> {t("manage_categories", "dashboard")}
          </Button>
          <Button variant="outline" onClick={fetchData} data-testid="refresh-data-button">
            <RefreshCw className="h-4 w-4 mr-2" /> {t("refresh", "common")}
          </Button>
        </div>

        {/* Transactions Table */}
        <Card data-testid="transactions-table-section">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>{t("transactions", "dashboard")}</CardTitle>
              <CardDescription>{t("your_transactions_for", "dashboard")} {t(MONTH_KEYS[selectedMonth - 1], "dashboard")} {selectedYear}</CardDescription>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="column-visibility-button">
                  <SlidersHorizontal className="h-4 w-4 mr-2" />
                  {t("columns", "dashboard")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>{t("show_hide_columns", "dashboard")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {columnDefs.map(col => {
                  // Skip converted column if no preferred currency
                  if (col.requiresPreferredCurrency && !userSettings?.preferred_currency) {
                    return null;
                  }
                  return (
                    <DropdownMenuCheckboxItem
                      key={col.key}
                      checked={visibleColumns[col.key]}
                      onCheckedChange={() => toggleColumn(col.key)}
                      data-testid={`column-toggle-${col.key}`}
                    >
                      {t(col.labelKey, "dashboard")}
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </CardHeader>
          
          {/* Search Bar */}
          <div className="px-6 pb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("search_transactions", "dashboard") || "Search by name, category, type, amount, status..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 max-w-sm"
                data-testid="transaction-search-input"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setSearchQuery("")}
                  data-testid="clear-search-button"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
          
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : overview?.transactions?.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">{t("no_transactions", "dashboard")}</p>
                <Button className="mt-4" onClick={() => setShowTransactionDialog(true)} data-testid="empty-state-add-button">
                  <Plus className="h-4 w-4 mr-2" /> {t("add_transaction", "dashboard")}
                </Button>
              </div>
            ) : (
              <Table data-testid="transactions-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={overview?.transactions?.length > 0 && selectedRows.size === overview?.transactions?.length}
                        onCheckedChange={() => {
                          if (selectedRows.size === overview?.transactions?.length) {
                            setSelectedRows(new Set());
                          } else {
                            setSelectedRows(new Set(overview?.transactions?.map(tx => tx.id) || []));
                          }
                        }}
                        aria-label="Select all"
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    {visibleColumns.date && (
                      <TableHead 
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('date')}
                        data-testid="sort-date"
                      >
                        <div className="flex items-center">
                          {t("date", "dashboard")} {getSortIcon('date')}
                        </div>
                      </TableHead>
                    )}
                    {visibleColumns.name && (
                      <TableHead 
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('name')}
                        data-testid="sort-name"
                      >
                        <div className="flex items-center">
                          {t("name", "dashboard")} {getSortIcon('name')}
                        </div>
                      </TableHead>
                    )}
                    {visibleColumns.category && (
                      <TableHead 
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('category')}
                        data-testid="sort-category"
                      >
                        <div className="flex items-center">
                          {t("category", "dashboard")} {getSortIcon('category')}
                        </div>
                      </TableHead>
                    )}
                    {visibleColumns.type && (
                      <TableHead 
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('type')}
                        data-testid="sort-type"
                      >
                        <div className="flex items-center">
                          {t("type", "dashboard")} {getSortIcon('type')}
                        </div>
                      </TableHead>
                    )}
                    {visibleColumns.original && (
                      <TableHead 
                        className="text-right cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('original')}
                        data-testid="sort-original"
                      >
                        <div className="flex items-center justify-end">
                          {t("original", "dashboard")} {getSortIcon('original')}
                        </div>
                      </TableHead>
                    )}
                    {userSettings?.preferred_currency && visibleColumns.converted && (
                      <TableHead 
                        className="text-right cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('converted')}
                        data-testid="sort-converted"
                      >
                        <div className="flex items-center justify-end">
                          {t("converted", "dashboard")} {getSortIcon('converted')}
                        </div>
                      </TableHead>
                    )}
                    {visibleColumns.balance && (
                      <TableHead 
                        className="text-right cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('balance')}
                        data-testid="sort-balance"
                      >
                        <div className="flex items-center justify-end">
                          {t("balance", "dashboard")} {getSortIcon('balance')}
                        </div>
                      </TableHead>
                    )}
                    {visibleColumns.status && (
                      <TableHead 
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('status')}
                        data-testid="sort-status"
                      >
                        <div className="flex items-center">
                          {t("status", "dashboard")} {getSortIcon('status')}
                        </div>
                      </TableHead>
                    )}
                    {visibleColumns.recurring && (
                      <TableHead 
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('recurring')}
                        data-testid="sort-recurring"
                      >
                        <div className="flex items-center">
                          {t("recurring", "dashboard")} {getSortIcon('recurring')}
                        </div>
                      </TableHead>
                    )}
                    {visibleColumns.nextDue && (
                      <TableHead 
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('nextDue')}
                        data-testid="sort-nextDue"
                      >
                        <div className="flex items-center">
                          {t("next_due", "dashboard")} {getSortIcon('nextDue')}
                        </div>
                      </TableHead>
                    )}
                    {visibleColumns.description && (
                      <TableHead 
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('description')}
                        data-testid="sort-description"
                      >
                        <div className="flex items-center">
                          {t("description", "dashboard")} {getSortIcon('description')}
                        </div>
                      </TableHead>
                    )}
                    {visibleColumns.actions && (
                      <TableHead className="text-center">{t("actions", "common")}</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    // Helper to get currency symbol
                    const getCurrencySymbol = (code) => {
                      const currency = currencies.find(c => c.code === code);
                      return currency?.symbol || code;
                    };

                    const preferredCurrency = userSettings?.preferred_currency;

                    // Calculate running balance
                    let runningBalance = 0;
                    const sortedByDate = [...(overview?.transactions || [])].sort((a, b) =>
                      new Date(a.date) - new Date(b.date)
                    );

                    // Calculate running balance for each transaction (exclude skipped)
                    // Use converted_amount if preferred_currency is set, otherwise use original_amount
                    const transactionsWithBalance = sortedByDate.map(tx => {
                      if (tx.status !== 'skipped') {
                        const amountToUse = preferredCurrency && tx.converted_amount != null
                          ? tx.converted_amount
                          : (tx.original_amount || tx.amount);
                        if (tx.type === 'income') {
                          runningBalance += amountToUse;
                        } else {
                          runningBalance -= amountToUse;
                        }
                      }
                      return { ...tx, runningBalance };
                    });

                    // Filter transactions by search query
                    let filteredTransactions = [...transactionsWithBalance];
                    
                    if (searchQuery.trim()) {
                      const query = searchQuery.toLowerCase().trim();
                      filteredTransactions = filteredTransactions.filter(tx => {
                        // Search by name
                        if (tx.name?.toLowerCase().includes(query)) return true;
                        
                        // Search by category name
                        if (tx.category_name?.toLowerCase().includes(query)) return true;
                        
                        // Search by type (income/expense)
                        if (tx.type?.toLowerCase().includes(query)) return true;
                        
                        // Search by amount (original or converted)
                        const amountStr = (tx.original_amount || tx.amount || 0).toString();
                        const convertedStr = (tx.converted_amount || 0).toString();
                        if (amountStr.includes(query) || convertedStr.includes(query)) return true;
                        
                        // Search by status - compute display status like getStatus() does
                        let displayStatus = '';
                        if (tx.status === 'scheduled') {
                          displayStatus = 'scheduled';
                        } else if (tx.status === 'skipped') {
                          displayStatus = 'skipped';
                        } else if (tx.status === 'paid' || tx.paid) {
                          displayStatus = 'paid';
                        } else {
                          const txDate = parseISO(tx.date);
                          const today = new Date();
                          if (tx.type === 'income') {
                            displayStatus = (isBefore(txDate, today) && !isToday(txDate)) ? 'pending' : 'upcoming';
                          } else {
                            if (isBefore(txDate, today) && !isToday(txDate)) {
                              displayStatus = 'overdue';
                            } else if (isToday(txDate)) {
                              displayStatus = 'due today duetoday due_today';
                            } else {
                              displayStatus = 'upcoming';
                            }
                          }
                        }
                        if (displayStatus.toLowerCase().includes(query)) return true;
                        
                        // Search by recurring frequency (monthly, weekly, yearly)
                        if (tx.is_recurring && tx.frequency?.toLowerCase().includes(query)) return true;
                        
                        // Search by description
                        if (tx.description?.toLowerCase().includes(query)) return true;
                        
                        return false;
                      });
                    }

                    // Apply user sorting or default to newest first
                    let displayTransactions = [...filteredTransactions];
                    
                    if (sortConfig.key) {
                      displayTransactions.sort((a, b) => {
                        let aVal, bVal;
                        
                        switch (sortConfig.key) {
                          case 'date':
                            aVal = new Date(a.date);
                            bVal = new Date(b.date);
                            break;
                          case 'name':
                            aVal = (a.name || '').toLowerCase();
                            bVal = (b.name || '').toLowerCase();
                            break;
                          case 'category':
                            aVal = (a.category_name || '').toLowerCase();
                            bVal = (b.category_name || '').toLowerCase();
                            break;
                          case 'type':
                            aVal = a.type;
                            bVal = b.type;
                            break;
                          case 'original':
                            aVal = a.original_amount || a.amount || 0;
                            bVal = b.original_amount || b.amount || 0;
                            break;
                          case 'converted':
                            aVal = a.converted_amount || 0;
                            bVal = b.converted_amount || 0;
                            break;
                          case 'balance':
                            aVal = a.runningBalance || 0;
                            bVal = b.runningBalance || 0;
                            break;
                          case 'status':
                            aVal = a.status || (a.paid ? 'paid' : 'pending');
                            bVal = b.status || (b.paid ? 'paid' : 'pending');
                            break;
                          case 'recurring':
                            aVal = a.is_recurring ? 1 : 0;
                            bVal = b.is_recurring ? 1 : 0;
                            break;
                          case 'nextDue':
                            aVal = a.next_due_date ? new Date(a.next_due_date) : new Date(0);
                            bVal = b.next_due_date ? new Date(b.next_due_date) : new Date(0);
                            break;
                          case 'description':
                            aVal = (a.description || '').toLowerCase();
                            bVal = (b.description || '').toLowerCase();
                            break;
                          default:
                            return 0;
                        }
                        
                        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                        return 0;
                      });
                    } else {
                      // Default: newest first
                      displayTransactions.reverse();
                    }

                    // Show empty state for search with no results
                    if (displayTransactions.length === 0 && searchQuery.trim()) {
                      return (
                        <TableRow>
                          <TableCell colSpan={12} className="h-32">
                            <div className="text-center py-8">
                              <Search className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
                              <p className="text-muted-foreground font-medium">{t("no_search_results", "dashboard") || "No transactions match your search"}</p>
                              <p className="text-sm text-muted-foreground/70 mt-1">Try adjusting your search terms</p>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="mt-3" 
                                onClick={() => setSearchQuery("")}
                                data-testid="clear-search-empty-button"
                              >
                                <X className="h-3 w-3 mr-2" />
                                {t("clear_search", "common") || "Clear Search"}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    }

                    return displayTransactions.map((tx) => {
                      // Determine status based on status field, paid, date, and type
                      const getStatus = () => {
                        // Check for scheduled optional transactions
                        if (tx.status === 'scheduled') {
                          return { labelKey: "scheduled", variant: "info", icon: Calendar, isScheduled: true };
                        }
                        if (tx.status === 'skipped') {
                          return { labelKey: "skipped", variant: "muted", icon: X, isSkipped: true };
                        }
                        if (tx.status === 'paid' || tx.paid) {
                          return { labelKey: "paid", variant: "success", icon: Check };
                        }

                        const txDate = parseISO(tx.date);
                        const today = new Date();

                        if (tx.type === 'income') {
                          // For income, check if it's pending (not received yet)
                          if (isBefore(txDate, today) && !isToday(txDate)) {
                            return { labelKey: "pending", variant: "warning", icon: Clock };
                          }
                          return { labelKey: "upcoming", variant: "info", icon: Calendar };
                        }

                        // For expenses
                        if (isBefore(txDate, today) && !isToday(txDate)) {
                          return { labelKey: "overdue", variant: "destructive", icon: AlertTriangle };
                        }
                        if (isToday(txDate)) {
                          return { labelKey: "due_today", variant: "warning", icon: Clock };
                        }
                        return { labelKey: "upcoming", variant: "info", icon: Calendar };
                      };

                      const status = getStatus();
                      const StatusIcon = status.icon;

                      const statusStyles = {
                        success: "bg-[hsl(var(--ff-success)/.15)] text-[hsl(var(--ff-success))] hover:bg-[hsl(var(--ff-success)/.25)]",
                        warning: "bg-[hsl(var(--ff-warning)/.15)] text-[hsl(var(--ff-warning))] hover:bg-[hsl(var(--ff-warning)/.25)]",
                        destructive: "bg-[hsl(var(--ff-expense)/.15)] text-[hsl(var(--ff-expense))] hover:bg-[hsl(var(--ff-expense)/.25)]",
                        info: "bg-[hsl(var(--ff-info)/.15)] text-[hsl(var(--ff-info))] hover:bg-[hsl(var(--ff-info)/.25)]",
                        muted: "bg-muted text-muted-foreground hover:bg-muted/80"
                      };

                      // Handle status update for scheduled transactions
                      const handleStatusAction = async (newStatus) => {
                        try {
                          await api.patch(`/transactions/${tx.id}/status?status=${newStatus}`);
                          toast.success(t("payment_updated", "dashboard"));
                          fetchData();
                        } catch (error) {
                          toast.error(t("failed_to_save", "errors"));
                        }
                      };

                      const originalCurrency = tx.currency || 'USD';
                      const originalSymbol = getCurrencySymbol(originalCurrency);
                      const preferredSymbol = preferredCurrency ? getCurrencySymbol(preferredCurrency) : null;

                      return (
                        <TableRow
                          key={tx.id}
                          className={`
                            ${tx.type === 'income' ? 'hover:bg-[hsla(var(--ff-income),.04)]' : 'hover:bg-[hsla(var(--ff-expense),.04)]'}
                            ${tx.status === 'skipped' ? 'opacity-50' : ''}
                            ${selectedRows.has(tx.id) ? 'bg-muted/30' : ''}
                          `}
                        >
                          <TableCell className="w-[50px]">
                            <Checkbox
                              checked={selectedRows.has(tx.id)}
                              onCheckedChange={() => toggleRowSelection(tx.id)}
                              aria-label={`Select ${tx.name}`}
                              data-testid={`select-row-${tx.id}`}
                            />
                          </TableCell>
                          {visibleColumns.date && (
                            <TableCell className="font-medium">
                              {formatDateUTC(tx.date)}
                            </TableCell>
                          )}
                          {visibleColumns.name && (
                            <TableCell>
                              <span className="font-medium">{tx.name || "Unnamed"}</span>
                            </TableCell>
                          )}
                          {visibleColumns.category && (
                            <TableCell>
                              {tx.category_name ? (
                                <Badge variant="outline" className="bg-[hsl(var(--ff-blue-steel)/.1)]">{tx.category_name}</Badge>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          )}
                          {visibleColumns.type && (
                            <TableCell>
                              <Badge className={tx.type === 'income' ? 'income-bg income-text border-0' : 'expense-bg expense-text border-0'}>
                                {tx.type}
                              </Badge>
                            </TableCell>
                          )}
                          {visibleColumns.original && (
                            <TableCell className={`text-right font-numeric font-semibold ${tx.type === 'income' ? 'income-text' : 'expense-text'}`}>
                              <span className={tx.status === 'skipped' ? 'line-through' : ''}>
                                {tx.type === 'income' ? '+' : '-'}{originalSymbol}{tx.original_amount?.toFixed(2) || tx.amount.toFixed(2)}
                              </span>
                              {originalCurrency !== 'USD' && (
                                <span className="text-xs text-muted-foreground ml-1">({originalCurrency})</span>
                              )}
                            </TableCell>
                          )}
                          {preferredCurrency && visibleColumns.converted && (
                            <TableCell className={`text-right font-numeric ${tx.type === 'income' ? 'income-text' : 'expense-text'}`}>
                              {tx.converted_amount != null ? (
                                <span className={tx.status === 'skipped' ? 'line-through opacity-50' : ''}>
                                  {tx.type === 'income' ? '+' : '-'}{preferredSymbol}{tx.converted_amount.toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          )}
                          {visibleColumns.balance && (
                            <TableCell className={`text-right font-numeric font-semibold ${tx.runningBalance >= 0 ? 'income-text' : 'expense-text'}`}>
                              {(() => {
                                const balanceSymbol = preferredCurrency
                                  ? (preferredSymbol || '$')
                                  : (currencies.find(c => c.code === userSettings?.default_currency)?.symbol || '$');
                                return `${balanceSymbol}${tx.runningBalance.toFixed(2)}`;
                              })()}
                            </TableCell>
                          )}
                          {visibleColumns.status && (
                            <TableCell>
                              {status.isScheduled ? (
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleStatusAction('confirmed')}
                                    className="bg-[hsl(var(--ff-success)/.15)] text-[hsl(var(--ff-success))] hover:bg-[hsl(var(--ff-success)/.25)] text-xs px-2 py-1 h-auto"
                                    data-testid={`confirm-${tx.id}`}
                                  >
                                    <Check className="h-3 w-3 mr-1" />
                                    {t("confirmed", "dashboard")}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleStatusAction('skipped')}
                                    className="bg-muted text-muted-foreground hover:bg-muted/80 text-xs px-2 py-1 h-auto"
                                    data-testid={`skip-${tx.id}`}
                                  >
                                    <X className="h-3 w-3 mr-1" />
                                    {t("skipped", "dashboard")}
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleTogglePaid(tx.id)}
                                  className={`${statusStyles[status.variant]} text-xs px-2 py-1 h-auto`}
                                  data-testid={`toggle-paid-${tx.id}`}
                                >
                                  <StatusIcon className="h-3 w-3 mr-1" />
                                  {t(status.labelKey, "dashboard")}
                                </Button>
                              )}
                            </TableCell>
                          )}
                          {visibleColumns.recurring && (
                            <TableCell>
                              {tx.is_recurring ? (
                                <Badge variant="outline" className="bg-[hsl(var(--ff-info)/.1)] text-[hsl(var(--ff-info))] border-0">
                                  <RefreshCw className="h-3 w-3 mr-1" /> {t(tx.frequency, "dashboard")}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          )}
                          {visibleColumns.nextDue && (
                            <TableCell>
                              {tx.is_recurring && tx.next_due_date ? (
                                <span className="text-sm text-muted-foreground">
                                  {formatDateUTC(tx.next_due_date)}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          )}
                          {visibleColumns.description && (
                            <TableCell className="max-w-[150px] truncate" title={tx.description}>
                              {tx.description || <span className="text-muted-foreground">-</span>}
                            </TableCell>
                          )}
                          {visibleColumns.actions && (
                            <TableCell className="text-center">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`actions-menu-${tx.id}`}>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => { setEditingTransaction(tx); setShowTransactionDialog(true); }}
                                    data-testid={`edit-transaction-${tx.id}`}
                                  >
                                    <Edit className="h-4 w-4 mr-2" />
                                    {t("edit", "common")}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      try {
                                        await api.delete(`/transactions/${tx.id}`);
                                        toast.success(t("transaction_deleted", "dashboard"));
                                        fetchData();
                                      } catch (error) {
                                        toast.error(t("failed_delete_transaction", "errors"));
                                      }
                                    }}
                                    className="text-destructive focus:text-destructive"
                                    data-testid={`delete-transaction-${tx.id}`}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    {t("delete", "common")}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            )}

            {/* Selection Summary - Only visible when rows are selected */}
            {selectedRows.size > 0 && overview?.transactions && (
              <div className="mt-4 p-4 bg-muted/30 rounded-lg border" data-testid="selection-summary">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium">
                    {selectedRows.size} {selectedRows.size === 1 ? 'row' : 'rows'} selected
                  </p>
                  <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="clear-selection">
                    <X className="h-4 w-4 mr-1" />
                    {t("clear", "common") || "Clear"}
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(() => {
                    const preferredCurrency = userSettings?.preferred_currency;
                    const getCurrencySymbol = (code) => {
                      const currency = currencies.find(c => c.code === code);
                      return currency?.symbol || code;
                    };
                    
                    const selected = overview.transactions.filter(tx => selectedRows.has(tx.id));
                    let totalIncome = 0;
                    let totalExpenses = 0;
                    
                    selected.forEach(tx => {
                      const amount = (preferredCurrency && tx.converted_amount != null) 
                        ? tx.converted_amount 
                        : (tx.original_amount || tx.amount);
                      
                      if (tx.type === 'income') {
                        totalIncome += amount;
                      } else if (tx.type === 'expense') {
                        totalExpenses += amount;
                      }
                    });
                    
                    const balance = totalIncome - totalExpenses;
                    const displaySymbol = getCurrencySymbol(preferredCurrency || userSettings?.default_currency || 'USD');
                    
                    return (
                      <>
                        {/* Selected Income */}
                        <div className="p-3 bg-background rounded-lg border">
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-muted-foreground">{t("selected_income", "dashboard") || "Selected Income"}</p>
                            <TrendingUp className="h-3 w-3 text-green-500" />
                          </div>
                          <p className="text-lg font-bold mt-1 font-numeric text-green-600">
                            {displaySymbol}{totalIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                        
                        {/* Selected Expenses */}
                        <div className="p-3 bg-background rounded-lg border">
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-muted-foreground">{t("selected_expenses", "dashboard") || "Selected Expenses"}</p>
                            <TrendingDown className="h-3 w-3 text-red-500" />
                          </div>
                          <p className="text-lg font-bold mt-1 font-numeric text-red-600">
                            {displaySymbol}{totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                        
                        {/* Selected Balance */}
                        <div className="p-3 bg-background rounded-lg border">
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-muted-foreground">{t("selected_balance", "dashboard") || "Selected Balance"}</p>
                            <DollarSign className="h-3 w-3 text-muted-foreground" />
                          </div>
                          <p className={`text-lg font-bold mt-1 font-numeric ${balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {displaySymbol}{balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Package Selection Dialog */}
      <PackageDialog
        open={showPackageDialog}
        onOpenChange={setShowPackageDialog}
        packages={packages}
        currentPackageId={user?.package_id}
        onSelect={handleSelectPackage}
      />

      {/* Transaction Dialog */}
      <TransactionDialog
        open={showTransactionDialog}
        onOpenChange={(open) => { setShowTransactionDialog(open); if (!open) setEditingTransaction(null); }}
        categories={categories}
        subgroups={subgroups}
        transaction={editingTransaction}
        onSuccess={() => { fetchData(); setEditingTransaction(null); }}
        currencies={currencies}
        userSettings={userSettings}
      />

      {/* Category & Subgroup Dialog */}
      <CategoryDialog
        open={showCategoryDialog}
        onOpenChange={setShowCategoryDialog}
        categories={categories}
        subgroups={subgroups}
        onSuccess={fetchData}
      />
    </div>
  );
}

// Package Selection Dialog Component
function PackageDialog({ open, onOpenChange, packages, currentPackageId, onSelect }) {
  const { t } = useLanguage();
  const [selectedPackage, setSelectedPackage] = useState(currentPackageId || "");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    if (!selectedPackage) return;
    setIsLoading(true);
    await onSelect(selectedPackage);
    setIsLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="package-selection-dialog">
        <DialogHeader>
          <DialogTitle>{t("select_package", "dashboard")}</DialogTitle>
          <DialogDescription>{t("package_required_desc", "dashboard")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-4">
          {packages.map((pkg) => (
            <Card
              key={pkg.id}
              className={`cursor-pointer transition-all ${selectedPackage === pkg.id ? 'border-[hsl(var(--ff-primary))] ring-2 ring-[hsl(var(--ff-primary)/.2)]' : 'hover:border-muted-foreground/50'}`}
              onClick={() => setSelectedPackage(pkg.id)}
              data-testid={`package-option-${pkg.id}`}
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-semibold">{pkg.name}</p>
                  <p className="text-sm text-muted-foreground">{pkg.description}</p>
                </div>
                <Badge variant={selectedPackage === pkg.id ? "default" : "outline"}>
                  {pkg.monthly_limit === 0 ? t("unlimited", "common") : `${pkg.monthly_limit} ${t("per_month", "auth")}`}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={handleSubmit} disabled={!selectedPackage || isLoading} data-testid="select-package-button">
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t("select", "common")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Transaction Dialog Component
function TransactionDialog({ open, onOpenChange, categories, subgroups, transaction, onSuccess, currencies, userSettings }) {
  const { t } = useLanguage();
  const isEditing = !!transaction;
  const [isLoading, setIsLoading] = useState(false);
  const [transactionNames, setTransactionNames] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const defaultCurrency = userSettings?.default_currency || "USD";
  const [formData, setFormData] = useState({
    name: "",
    category_id: "",
    amount: "",
    currency: defaultCurrency,
    description: "",
    date: new Date().toISOString().split("T")[0],
    type: "expense",
    paid: false,
    is_recurring: false,
    is_optional: false,
    frequency: "monthly",
    base_due_date: 1,
    day_of_week: new Date().getDay() === 0 ? 6 : new Date().getDay() - 1, // Convert JS day (0=Sun) to Python day (0=Mon)
    recurring_month: new Date().getMonth() + 1 // 1-12
  });

  // Day of week options (Python convention: 0=Monday to 6=Sunday)
  const daysOfWeek = [
    { value: 0, label: "Monday" },
    { value: 1, label: "Tuesday" },
    { value: 2, label: "Wednesday" },
    { value: 3, label: "Thursday" },
    { value: 4, label: "Friday" },
    { value: 5, label: "Saturday" },
    { value: 6, label: "Sunday" }
  ];

  // Month options
  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
  ];

  // Fetch transaction names for autocomplete
  useEffect(() => {
    if (open) {
      api.get("/transaction-names").then(res => {
        setTransactionNames(res.data || []);
      }).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (transaction) {
      // Convert JS day (0=Sun) to Python day (0=Mon) for current day default
      const currentJsDay = new Date().getDay();
      const currentPyDay = currentJsDay === 0 ? 6 : currentJsDay - 1;
      
      setFormData({
        name: transaction.name || "",
        category_id: transaction.category_id || "",
        amount: transaction.amount.toString(),
        currency: transaction.currency || defaultCurrency,
        description: transaction.description || "",
        date: transaction.date.split("T")[0],
        type: transaction.type,
        paid: transaction.paid,
        is_recurring: transaction.is_recurring,
        is_optional: transaction.is_optional || false,
        frequency: transaction.frequency || "monthly",
        base_due_date: transaction.base_due_date || 1,
        day_of_week: transaction.day_of_week ?? currentPyDay,
        recurring_month: transaction.recurring_month || (new Date().getMonth() + 1)
      });
    } else {
      // Convert JS day (0=Sun) to Python day (0=Mon)
      const currentJsDay = new Date().getDay();
      const currentPyDay = currentJsDay === 0 ? 6 : currentJsDay - 1;
      
      setFormData({
        name: "",
        category_id: "",
        amount: "",
        currency: defaultCurrency,
        description: "",
        date: new Date().toISOString().split("T")[0],
        type: "expense",
        paid: false,
        is_recurring: false,
        is_optional: false,
        frequency: "monthly",
        base_due_date: 1,
        day_of_week: currentPyDay,
        recurring_month: new Date().getMonth() + 1
      });
    }
  }, [transaction, open, defaultCurrency]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.amount) {
      toast.error(t("required", "common"));
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        date: new Date(formData.date).toISOString(),
        category_id: formData.category_id || null
      };
      
      // Set recurring-specific fields based on frequency
      if (formData.is_recurring) {
        payload.frequency = formData.frequency;
        payload.is_optional = formData.is_optional;
        
        if (formData.frequency === "weekly") {
          payload.day_of_week = parseInt(formData.day_of_week);
          payload.base_due_date = null;
          payload.recurring_month = null;
        } else if (formData.frequency === "yearly") {
          payload.base_due_date = parseInt(formData.base_due_date);
          payload.recurring_month = parseInt(formData.recurring_month);
          payload.day_of_week = null;
        } else { // monthly
          payload.base_due_date = parseInt(formData.base_due_date);
          payload.day_of_week = null;
          payload.recurring_month = null;
        }
      } else {
        payload.frequency = null;
        payload.is_optional = false;
        payload.base_due_date = null;
        payload.day_of_week = null;
        payload.recurring_month = null;
      }

      if (isEditing) {
        await api.put(`/transactions/${transaction.id}`, payload);
        toast.success(t("transaction_updated", "dashboard"));
      } else {
        await api.post("/transactions", payload);
        toast.success(t("transaction_added", "dashboard"));
      }
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t("failed_to_save", "errors"));
    } finally {
      setIsLoading(false);
    }
  };

  // Filter name suggestions based on input
  const filteredNameSuggestions = transactionNames.filter(name =>
    name.toLowerCase().includes(formData.name.toLowerCase()) && name.toLowerCase() !== formData.name.toLowerCase()
  ).slice(0, 5);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="transaction-dialog">
        <DialogHeader>
          <DialogTitle>{isEditing ? t("edit_transaction", "dashboard") : t("add_new_transaction", "dashboard")}</DialogTitle>
          <DialogDescription>
            {isEditing ? t("edit_transaction", "dashboard") : t("add_first_transaction", "dashboard")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type Selection */}
          <div className="space-y-2">
            <Label>{t("type", "dashboard")}</Label>
            <RadioGroup
              value={formData.type}
              onValueChange={(value) => setFormData({ ...formData, type: value })}
              className="flex gap-4"
              data-testid="transaction-type-radio"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="expense" id="expense" />
                <Label htmlFor="expense" className="cursor-pointer">{t("expense", "dashboard")}</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="income" id="income" />
                <Label htmlFor="income" className="cursor-pointer">{t("income", "dashboard")}</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Name - Free text with autocomplete suggestions */}
          <div className="space-y-2 relative">
            <Label>{t("name", "dashboard")} *</Label>
            <Input
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                setShowNameSuggestions(true);
              }}
              onFocus={() => setShowNameSuggestions(true)}
              onBlur={() => setTimeout(() => setShowNameSuggestions(false), 200)}
              placeholder="e.g., Grocery Shopping, Netflix, Salary"
              data-testid="transaction-name-input"
              required
            />
            {showNameSuggestions && filteredNameSuggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md">
                {filteredNameSuggestions.map((name, idx) => (
                  <div
                    key={idx}
                    className="px-3 py-2 hover:bg-accent cursor-pointer text-sm"
                    onMouseDown={() => {
                      setFormData({ ...formData, name });
                      setShowNameSuggestions(false);
                    }}
                  >
                    {name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Category (formerly Subgroup) */}
          <div className="space-y-2">
            <Label>{t("category", "dashboard")} <span className="text-muted-foreground text-xs">({t("optional", "common")})</span></Label>
            <Select value={formData.category_id || "none"} onValueChange={(value) => setFormData({ ...formData, category_id: value === "none" ? "" : value })}>
              <SelectTrigger data-testid="transaction-category-select">
                <SelectValue placeholder={t("select_category", "dashboard")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("no_category", "dashboard")}</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Amount, Currency & Date */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>{t("amount", "dashboard")} *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                data-testid="transaction-amount-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>{t("currency", "dashboard")}</Label>
              <Select
                value={formData.currency || userSettings?.default_currency || "USD"}
                onValueChange={(value) => setFormData({ ...formData, currency: value })}
              >
                <SelectTrigger data-testid="transaction-currency-select">
                  <SelectValue placeholder="Currency" />
                </SelectTrigger>
                <SelectContent>
                  {(userSettings?.enabled_currencies || ["USD"]).map((code) => {
                    const curr = currencies.find(c => c.code === code);
                    return (
                      <SelectItem key={code} value={code}>
                        {curr?.symbol || code} {code}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("date", "dashboard")}</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                data-testid="transaction-date-input"
                required
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>{t("description", "dashboard")} ({t("optional", "common")})</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g., Monthly electricity bill"
              data-testid="transaction-description-input"
            />
          </div>

          {/* Paid Status */}
          <div className="flex items-center justify-between">
            <Label htmlFor="paid">{t("mark_as", "dashboard")} {t("paid", "dashboard")}</Label>
            <Switch
              id="paid"
              checked={formData.paid}
              onCheckedChange={(checked) => setFormData({ ...formData, paid: checked })}
              data-testid="transaction-paid-switch"
            />
          </div>

          {/* Recurring */}
          <div className="flex items-center justify-between">
            <Label htmlFor="recurring">{t("recurring", "dashboard")}</Label>
            <Switch
              id="recurring"
              checked={formData.is_recurring}
              onCheckedChange={(checked) => {
                // Auto-populate base_due_date from the transaction date when enabling recurring
                const dayFromDate = formData.date ? new Date(formData.date).getDate() : 1;
                setFormData({
                  ...formData,
                  is_recurring: checked,
                  base_due_date: checked ? dayFromDate : formData.base_due_date
                });
              }}
              data-testid="transaction-recurring-switch"
            />
          </div>

          {formData.is_recurring && (
            <div className="space-y-4 p-3 bg-muted rounded-lg">
              <div className="space-y-2">
                <Label>{t("frequency", "dashboard")}</Label>
                <Select value={formData.frequency} onValueChange={(value) => setFormData({ ...formData, frequency: value })}>
                  <SelectTrigger data-testid="transaction-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">{t("weekly", "dashboard")}</SelectItem>
                    <SelectItem value="monthly">{t("monthly", "dashboard")}</SelectItem>
                    <SelectItem value="yearly">{t("yearly", "dashboard")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Weekly: Show day of week selector */}
              {formData.frequency === "weekly" && (
                <div className="space-y-2">
                  <Label>{t("day_of_week", "dashboard")}</Label>
                  <Select 
                    value={formData.day_of_week?.toString()} 
                    onValueChange={(value) => setFormData({ ...formData, day_of_week: parseInt(value) })}
                  >
                    <SelectTrigger data-testid="transaction-day-of-week-select">
                      <SelectValue placeholder="Select day" />
                    </SelectTrigger>
                    <SelectContent>
                      {daysOfWeek.map(day => (
                        <SelectItem key={day.value} value={day.value.toString()}>{day.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Repeats every week on this day</p>
                </div>
              )}
              
              {/* Monthly: Show day of month selector */}
              {formData.frequency === "monthly" && (
                <div className="space-y-2">
                  <Label>{t("day_of_month", "dashboard")}</Label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.base_due_date}
                    onChange={(e) => setFormData({ ...formData, base_due_date: e.target.value })}
                    data-testid="transaction-due-day-input"
                  />
                  <p className="text-xs text-muted-foreground">Repeats on this day each month</p>
                </div>
              )}
              
              {/* Yearly: Show month and day selectors */}
              {formData.frequency === "yearly" && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("month", "dashboard")}</Label>
                    <Select 
                      value={formData.recurring_month?.toString()} 
                      onValueChange={(value) => setFormData({ ...formData, recurring_month: parseInt(value) })}
                    >
                      <SelectTrigger data-testid="transaction-recurring-month-select">
                        <SelectValue placeholder="Select month" />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map(month => (
                          <SelectItem key={month.value} value={month.value.toString()}>{month.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("date", "dashboard")}</Label>
                    <Input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.base_due_date}
                      onChange={(e) => setFormData({ ...formData, base_due_date: e.target.value })}
                      data-testid="transaction-yearly-day-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground col-span-2">Repeats on this date each year</p>
                </div>
              )}
              
              <div className="flex items-center justify-between pt-2 border-t border-border/50">
                <div>
                  <Label htmlFor="optional">{t("optional_recurring", "dashboard")}</Label>
                  <p className="text-xs text-muted-foreground">{t("optional_recurring", "dashboard")}</p>
                </div>
                <Switch
                  id="optional"
                  checked={formData.is_optional || false}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_optional: checked })}
                  data-testid="transaction-optional-switch"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("cancel", "common")}
            </Button>
            <Button type="submit" disabled={isLoading} data-testid="transaction-submit-button">
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {isEditing ? t("update", "common") : t("add", "common")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Category & Subgroup Dialog Component
function CategoryDialog({ open, onOpenChange, categories, subgroups, onSuccess }) {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: "" });

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.name) return;
    setIsLoading(true);
    try {
      await api.post("/categories", { name: categoryForm.name, type: "expense" });
      toast.success(t("category_created", "dashboard"));
      setCategoryForm({ name: "" });
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("failed_to_save", "errors"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await api.delete(`/categories/${id}`);
      toast.success(t("category_deleted", "dashboard"));
      onSuccess();
    } catch (error) {
      toast.error(t("failed_to_delete", "errors"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="category-dialog">
        <DialogHeader>
          <DialogTitle>{t("category_management", "dashboard")}</DialogTitle>
          <DialogDescription>{t("manage_categories_desc", "dashboard")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Category Form */}
          <form onSubmit={handleCreateCategory} className="flex gap-2" data-testid="category-create-form">
            <Input
              value={categoryForm.name}
              onChange={(e) => setCategoryForm({ name: e.target.value })}
              placeholder={t("category_name", "dashboard")}
              data-testid="category-name-input"
              className="flex-1"
            />
            <Button type="submit" disabled={isLoading || !categoryForm.name} data-testid="category-form-submit-button">
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            </Button>
          </form>

          {/* Category List */}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            <p className="text-sm font-medium text-muted-foreground">{t("category", "dashboard")}</p>
            {categories.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">{t("no_categories", "dashboard")}. {t("create_first_category", "dashboard")}</p>
            ) : (
              categories.map((cat) => (
                <div key={cat.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`category-item-${cat.id}`}>
                  <div className="flex items-center gap-3">
                    <Tags className="h-4 w-4 text-muted-foreground" />
                    <p className="font-medium">{cat.name}</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteCategory(cat.id)} data-testid={`delete-category-${cat.id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

